import { Section, Sub, Card, Note, Fields, Code, Shot } from '../../../components/docs/Blocks';

export default function Addons() {
  return (
    <Section
      id="addons"
      title="Add-ons"
      lead="Two screens: the packs active in the open world, and the packs installed on the computer. Between them you can install, remove, reorder, update and repair add-ons without going near a folder."
    >
      <Sub id="world-addons" title="World Add-ons">
        <Shot
          name="addons-addons.world"
          alt="The World Add-ons screen listing behaviour and resource packs active in the world"
        />
        <Card>
          <p>
            The behaviour packs and resource packs this world has switched on, in load
            order. Since 3.1 they are in two lists, Behavior Packs and Resource Packs,
            each with its own order, as they were in version 2. The summary counts how
            many are active and how many need attention.
          </p>
          <Fields
            items={[
              ['Load order', 'Packs at the top have higher priority. Drag a pack up or down its list, use the arrows, or press Ctrl+Up and Ctrl+Down; the pack stays selected as it moves. Reordering preserves every other field in the world pack list, so nothing else about the pack changes.'],
              ['Details', 'The pack name, version, type, where its files are, and its declared dependencies resolved to readable names rather than raw uuids.'],
              ['Add to World', 'Adds an installed pack. Dependencies it declares are detected and offered alongside it.'],
              ['Remove', 'Takes the pack out of the world. A pack other active packs depend on cannot be removed until they are, and Toolchest names them. Removals can be undone before you save.'],
              ['Version changes', 'When the library holds a different version of an active pack, the card offers to update or downgrade the world to it.'],
              ['Import Add-on', 'Installs one or more .mcpack or .mcaddon files straight into the world.'],
            ]}
          />
        </Card>

        <h4 className="text-base font-semibold text-slate-900 dark:text-white mt-6 mb-3">
          Pack health
        </h4>
        <Card>
          <Fields
            items={[
              ['Missing files', 'The world references a pack whose files are not on this machine. This is normal for a world copied from a server or another computer, where the pack exists and works — so check before removing the reference, because removing it takes the pack out of the world.'],
              ['Dependency not active', 'A pack declares a dependency that this world does not have switched on.'],
              ['Beta APIs', 'The pack uses beta script APIs and may need experimental Minecraft features enabled.'],
              ['Marketplace', 'DRM-protected content, marked with a gold indicator and view-only. Toolchest will show it but cannot modify it.'],
            ]}
          />
        </Card>

        <h4 className="text-base font-semibold text-slate-900 dark:text-white mt-6 mb-3">
          Make Achievement Friendly
        </h4>
        <Card>
          <p>
            Adding a behaviour pack normally disables achievements for the world.
            Toolchest can patch a pack’s manifest so the game stops treating it as
            achievement-breaking, and marks packs that already carry that metadata.
          </p>
          <p>
            There is an equivalent for <strong>Vibrant Visuals</strong>: a resource
            pack without the PBR capability declared will not work with the new
            lighting, and Toolchest can add it.
          </p>
          <Note>
            Achievements also depend on the world never having had cheats enabled, and
            on experiments being off. The Gameplay screen’s <em>Has Been Loaded in
            Creative</em> flag is the other half of the answer.
          </Note>
        </Card>
      </Sub>

      <Sub id="minecraft-addons" title="Minecraft Add-ons">
        <Shot
          name="addons-addons.library"
          alt="The Minecraft Add-ons library listing every pack installed on the computer"
        />

        <Card>
          <p>
            Everything installed on the computer, found across every Minecraft profile
            — behaviour packs, resource packs, development packs and Marketplace
            content. This screen works without a world open, so you can tidy up your
            library at any time.
          </p>
          <Fields
            items={[
              ['The library', 'Every installed pack with its name, version, type and which profile it came from. Searchable, and filterable by pack type.'],
              ['Import to Minecraft', 'Installs one or more .mcpack or .mcaddon files globally. Selecting several at once is fine.'],
              ['Update in place', 'Importing a pack you already have shows exactly what will change — update, downgrade or reinstall, with both version numbers — before it does anything.'],
              ['Undo', 'An import or a removal can be undone until the next change to the library. The previous files are restored rather than re-downloaded.'],
              ['Add to World', 'With a world open, adds the selected pack to it. Staged like every other change.'],
              ['Remove from Minecraft', 'Deletes the installed files. A pack active in the open world has to be removed from the world first, and Toolchest says so rather than breaking the world.'],
              ['Open Folder', 'Opens the pack folder in Explorer.'],
            ]}
          />
          <Note>
            A pack whose manifest names a <Code>pack.name</Code> translation key rather
            than a name shows the key. Toolchest does not read pack language files, so
            it cannot resolve those — the uuid on the card identifies it either way.
          </Note>
        </Card>
      </Sub>
    </Section>
  );
}
